(() => {
  const SELECTOR = '[data-winter-bundle]';

  const unique = (values) => [...new Set(values.filter(Boolean))];

  const initBundle = (root) => {
    if (!root || root.dataset.bundleReady === 'true') return;

    const sectionId = root.dataset.winterBundle;
    const dialog = root.querySelector('[data-bundle-dialog]');
    const inlineBundle = document.querySelector(
      `[data-inline-winter-bundle="${CSS.escape(sectionId)}"]`
    );
    if (!dialog || !inlineBundle) return;

    const products = {};
    root.querySelectorAll('[data-bundle-product-json]').forEach((script) => {
      try {
        products[script.dataset.bundleProductJson] = JSON.parse(script.textContent);
      } catch (error) {
        console.error('Winter bundle product JSON is invalid.', error);
      }
    });

    const modalTitle = dialog.querySelector('[data-bundle-modal-title]');
    const modalOriginalPrice = dialog.querySelector('[data-bundle-modal-original-price]');
    const modalSalePrice = dialog.querySelector('[data-bundle-modal-sale-price]');
    const modalImage = dialog.querySelector('[data-bundle-modal-image]');
    const colorField = dialog.querySelector('[data-bundle-color-field]');
    const colorList = dialog.querySelector('[data-bundle-color]');
    const colorValue = dialog.querySelector('[data-bundle-color-value]');
    const sizeField = dialog.querySelector('[data-bundle-size-field]');
    const sizeList = dialog.querySelector('[data-bundle-size]');
    const sizeValue = dialog.querySelector('[data-bundle-size-value]');
    const confirmButton = dialog.querySelector('[data-bundle-confirm]');
    const closeButton = dialog.querySelector('[data-bundle-close]');
    const addButton = inlineBundle.querySelector('[data-bundle-add]');
    const errors = [
      inlineBundle.querySelector('[data-bundle-error]'),
      dialog.querySelector('[data-bundle-error]')
    ].filter(Boolean);

    const selections = {};
    const storageKey = `winter-bundle-selections:${sectionId}`;
    let activeKey = null;
    let activeColor = '';
    let activeSize = '';

    const showError = (message = '') => {
      errors.forEach((element) => {
        element.textContent = message;
        element.hidden = !message;
      });
    };

    const optionAt = (variant, position) =>
      position > 0 ? variant.options[position - 1] || '' : '';

    const variantsForColor = (product, color) =>
      product.variants.filter((variant) => {
        return !product.colorPosition || optionAt(variant, product.colorPosition) === color;
      });

    const findVariant = (product, color, size) =>
      product.variants.find((variant) => {
        const colorMatches =
          !product.colorPosition || optionAt(variant, product.colorPosition) === color;
        const sizeMatches =
          !product.sizePosition || optionAt(variant, product.sizePosition) === size;
        return colorMatches && sizeMatches;
      });

    const saveSelections = () => {
      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(selections));
      } catch (error) {
        console.warn('Winter bundle selection could not be saved.', error);
      }
    };

    const validateSelection = (key, selection) => {
      const product = products[key];
      if (!product || !selection?.id) return null;

      const variant = product.variants.find(
        (item) => String(item.id) === String(selection.id)
      );
      if (!variant || !variant.available) return null;

      const color = optionAt(variant, product.colorPosition);
      const size = optionAt(variant, product.sizePosition) || 'One size';

      if (product.colorPosition && color !== selection.color) return null;
      if (product.sizePosition && size !== selection.size) return null;

      return { id: variant.id, color, size };
    };

    const updateSelectionSummary = (key) => {
      const trigger = inlineBundle.querySelector(
        `[data-bundle-trigger="${key}"]`
      );
      const summary = trigger?.querySelector('[data-bundle-selection]');
      const selection = selections[key];
      if (!summary) return;

      summary.textContent = selection
        ? [selection.color, selection.size === 'One size' ? '' : selection.size]
            .filter(Boolean)
            .join(' / ')
        : '';
    };

    const syncBundleState = () => {
      Object.keys(products).forEach(updateSelectionSummary);
      addButton.hidden = !Object.keys(products).every(
        (key) => Boolean(selections[key])
      );
    };

    const restoreSelections = () => {
      let stored = {};
      try {
        stored = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
      } catch (error) {
        stored = {};
      }

      Object.keys(products).forEach((key) => {
        const validSelection = validateSelection(key, stored[key]);
        if (validSelection) selections[key] = validSelection;
      });

      saveSelections();
      syncBundleState();
    };

    const setProductImage = (product, color) => {
      const imageVariant = variantsForColor(product, color).find(
        (variant) => variant.image
      );
      const src = imageVariant?.image || product.featuredImage || '';
      modalImage.src = src;
      modalImage.alt = product.title || '';
    };

    const renderSizes = (product) => {
      sizeList.innerHTML = '';
      activeSize = '';
      sizeValue.textContent = '';

      const colorVariants = variantsForColor(product, activeColor);
      const sizes = product.sizePosition
        ? unique(colorVariants.map((variant) => optionAt(variant, product.sizePosition)))
        : ['One size'];

      sizeField.hidden = sizes.length === 0;

      sizes.forEach((size) => {
        const variant = product.sizePosition
          ? findVariant(product, activeColor, size)
          : colorVariants[0];
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'bundle-modal__size';
        button.textContent = size;
        button.dataset.value = size;
        button.disabled = !variant || !variant.available;
        button.setAttribute('aria-pressed', 'false');

        button.addEventListener('click', () => {
          if (button.disabled) return;
          activeSize = size;
          sizeValue.textContent = size;
          sizeList.querySelectorAll('button').forEach((item) => {
            item.setAttribute('aria-pressed', String(item === button));
          });
          showError();
        });
        sizeList.appendChild(button);
      });

      const firstAvailable = sizeList.querySelector('button:not(:disabled)');
      if (firstAvailable && sizes.length === 1) firstAvailable.click();
    };

    const selectColor = (product, color, button) => {
      activeColor = color;
      colorValue.textContent = color;
      colorList.querySelectorAll('button').forEach((item) => {
        item.setAttribute('aria-pressed', String(item === button));
      });
      setProductImage(product, color);
      renderSizes(product);
      showError();
    };

    const renderColors = (product) => {
      colorList.innerHTML = '';
      const colors = product.colorPosition
        ? product.colors
        : [{ value: '', swatch: '' }];
      colorField.hidden = !product.colorPosition || colors.length === 0;

      colors.forEach((color) => {
        const button = document.createElement('button');
        const hasAvailableVariant = variantsForColor(product, color.value).some(
          (variant) => variant.available
        );
        button.type = 'button';
        button.className = 'bundle-modal__swatch';
        button.dataset.value = color.value;
        button.title = color.value;
        button.setAttribute('aria-label', color.value);
        button.setAttribute('aria-pressed', 'false');
        button.disabled = !hasAvailableVariant;
        if (color.swatch) button.style.backgroundImage = `url("${color.swatch}")`;
        button.addEventListener('click', () => {
          if (!button.disabled) selectColor(product, color.value, button);
        });
        colorList.appendChild(button);
      });

      const firstAvailable = colorList.querySelector('button:not(:disabled)');
      if (firstAvailable) firstAvailable.click();
      else {
        activeColor = '';
        renderSizes(product);
      }
    };

    const formatMoney = (cents) => {
      const amount = Number(cents || 0) / 100;
      const currency = window.Shopify?.currency?.active || 'USD';
      const locale = document.documentElement.lang || 'en-US';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    };

    const renderBundlePrice = (product) => {
      const originalPrice = Number(product?.price || 0);
      const discountedPrice = Math.round(originalPrice * 0.8);

      if (modalOriginalPrice) {
        modalOriginalPrice.textContent = formatMoney(originalPrice);
      }

      if (modalSalePrice) {
        modalSalePrice.textContent = formatMoney(discountedPrice);
      }
    };

    const openDialog = (key) => {
      const product = products[key];
      if (!product) return;
      activeKey = key;
      modalTitle.textContent = (product.title || '').split('|')[0].trim();
      renderBundlePrice(product);
      modalImage.src = product.featuredImage || '';
      modalImage.alt = product.title || '';
      renderColors(product);

      const savedSelection = selections[key];
      if (savedSelection) {
        const savedColorButton = Array.from(
          colorList.querySelectorAll('button:not(:disabled)')
        ).find((button) => button.dataset.value === savedSelection.color);

        if (savedColorButton) savedColorButton.click();

        const savedSizeButton = Array.from(
          sizeList.querySelectorAll('button:not(:disabled)')
        ).find((button) => button.dataset.value === savedSelection.size);

        if (savedSizeButton) savedSizeButton.click();
      }

      showError();
      dialog.showModal();
      document.documentElement.classList.add('bundle-modal-open');
    };

    const closeDialog = () => {
      dialog.close();
      document.documentElement.classList.remove('bundle-modal-open');
    };

    inlineBundle.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-bundle-trigger]');
      if (trigger) openDialog(trigger.dataset.bundleTrigger);
    });

    confirmButton.addEventListener('click', () => {
      const product = products[activeKey];
      if (!product) return;
      if (product.colorPosition && !activeColor) {
        showError('Please choose a color.');
        return;
      }
      if (product.sizePosition && !activeSize) {
        showError('Please choose an available size.');
        return;
      }

      const variant = product.sizePosition
        ? findVariant(product, activeColor, activeSize)
        : variantsForColor(product, activeColor).find((item) => item.available);
      if (!variant?.available) {
        showError('This combination is unavailable.');
        return;
      }

      selections[activeKey] = {
        id: variant.id,
        color: activeColor,
        size: activeSize || 'One size'
      };
      saveSelections();
      syncBundleState();
      closeDialog();
    });

    addButton.addEventListener('click', async () => {
      const items = Object.keys(products).map((key) => ({
        id: selections[key]?.id,
        quantity: 1
      }));
      if (items.some((item) => !item.id)) {
        showError('Please choose a color and size for both products.');
        return;
      }

      addButton.disabled = true;
      showError();
      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const requestBody = { items };

        if (
          cartDrawer &&
          typeof cartDrawer.getSectionsToRender === 'function'
        ) {
          requestBody.sections = cartDrawer
            .getSectionsToRender()
            .map((section) => section.section);
          requestBody.sections_url = window.location.pathname;
        }

        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(requestBody)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to add this bundle.');

        if (
          cartDrawer &&
          result.sections &&
          typeof cartDrawer.renderContents === 'function'
        ) {
          /*
           * 使用主题自己的渲染方法一次性更新抽屉与购物车角标，
           * 第二个参数 true 会在渲染完成后自动打开抽屉。
           */
          await cartDrawer.renderContents(result, true);
        } else {
          /* 与当前主题 cart-drawer 组件保持一致的事件回退方案。 */
          document.dispatchEvent(
            new CustomEvent('dispatch:cart-drawer:refresh')
          );
          window.setTimeout(() => {
            document.dispatchEvent(
              new CustomEvent('dispatch:cart-drawer:open', {
                detail: { opener: addButton }
              })
            );
          }, 150);
        }
      } catch (error) {
        showError(error.message || 'Unable to add this bundle. Please try again.');
      } finally {
        addButton.disabled = false;
      }
    });

    closeButton.addEventListener('click', closeDialog);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('bundle-modal-open');
    });

    restoreSelections();

    root.dataset.bundleReady = 'true';
  };

  const initAll = () => document.querySelectorAll(SELECTOR).forEach(initBundle);
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', initAll)
    : initAll();
  document.addEventListener('shopify:section:load', initAll);
})();